import React from 'react';
import autobind from 'autobind';
import { observer, inject } from 'mobx-react';
import { observable, makeObservable } from 'mobx';
import { phoneStore } from 'react-sip-phone';

import { PhoneWidget, PhoneWidgetProps } from './PhoneWidget';
import { AppState } from '@store/App/App';
import { appService } from '@store/App/appService';
import { AuthUser } from '@store/AuthUser/AuthUser';
import { authUserService } from '@store/AuthUser/services/authUserService';
import { SipConfigurationsData } from 'chaika-frontend-types/frontend-cms';

interface Props extends PhoneWidgetProps {}

interface StoreProps {
    authUser: AuthUser;
    app: AppState;
}

interface PhoneStoreState {
    sipAccounts: { userAgent: { stop(): Promise<void> } | null };
}

const injectableStores: (keyof StoreProps)[] = [AppState.Name, AuthUser.Name];

@observer
export class Component extends React.Component<Props & StoreProps> {
    @observable
    private currentDialString = '';

    @observable
    private isOnLoad = true;

    private isConnecting = false;

    private requestedSipOrganizationId?: string;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public async componentDidMount(): Promise<void> {
        const { app, authUser } = this.props;

        if (!authUser.organizationId) {
            app.setSelectOrganizationPopupParams({});
        }

        await Promise.all([this.connectSipAccount(), appService.loadSipConfig()]);
        this.currentDialString = app.phoneWidget.initialPhoneNumber ?? '';
        this.isOnLoad = false;
    }

    public componentDidUpdate(): void {
        void this.connectSipAccount();
    }

    public render(): React.ReactNode {
        const {
            app: { sip = {} as SipConfigurationsData },
            authUser: { sipAccount, organizationId },
        } = this.props;
        return React.createElement(PhoneWidget, {
            ...sip,
            sipuri: sipAccount?.sipuri ?? '',
            password: sipAccount?.password ?? '',
            isOnLoad: this.isOnLoad || sipAccount?.organizationId !== organizationId,
            currentDialString: this.currentDialString,
            onDialpadButtonClick: this.handleDialpadButtonClick,
            onDialStringChange: this.handleDialStringChange,
            onStartNewSession: this.handleStartNewSession,
            onSessionTerminated: this.handleSessionTerminated,
        });
    }

    private async connectSipAccount(): Promise<void> {
        const { authUser } = this.props;

        if (
            this.isConnecting ||
            !authUser.organizationId ||
            this.requestedSipOrganizationId === authUser.organizationId ||
            authUser.sipAccount?.organizationId === authUser.organizationId
        ) {
            return;
        }

        this.isConnecting = true;
        this.requestedSipOrganizationId = authUser.organizationId;
        await (phoneStore.getState() as unknown as PhoneStoreState).sipAccounts.userAgent?.stop();
        await authUserService.initSipAccount();
        this.isConnecting = false;
    }

    @autobind
    private handleDialStringChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void {
        this.currentDialString = value;
    }

    @autobind
    private handleDialpadButtonClick(value: string): void {
        this.currentDialString = `${this.currentDialString}${value}`;
    }

    @autobind
    private handleStartNewSession(): void {
        this.props.app.phoneWidget.hasActiveSession = true;
    }

    @autobind
    private handleSessionTerminated(): void {
        this.props.app.phoneWidget.hasActiveSession = false;
    }
}

export const PhoneWidgetContainer = inject<Props, StoreProps>(...injectableStores)(Component);
